<script setup lang="ts">
import { ref } from "vue";
import type { ChatProps } from "./chat.types";
import { useChat } from "./useChat";
import { chatScrollClasses } from "./chat.styles";
import ChatVirtualItem from "./ChatVirtualItem.vue";

defineOptions({ name: "EChat" });

const props = withDefaults(defineProps<ChatProps>(), {
  messages: () => [],
  hasMore: false,
  loading: false,
  showAvatar: true,
  color: "primary",
});

const scrollCls = chatScrollClasses({});

const scrollContainerRef = ref<HTMLElement | null>(null);
const sentinelRef = ref<HTMLElement | null>(null);

const {
  classes,
  isArrayMode,
  virtualItems,
  totalHeight,
  topOffset,
  measureItem,
  appendMessage,
  appendMessages,
  prependMessages,
  updateMessage,
  clearMessages,
  scrollToBottom,
  scrollToMessage,
  formatTime,
  shouldShowDivider,
  formatDivider,
  loading,
  showAvatar,
} = useChat({ props, scrollContainerRef, sentinelRef });

defineExpose({
  appendMessage,
  appendMessages,
  prependMessages,
  updateMessage,
  clearMessages,
  scrollToBottom,
  scrollToMessage,
});
</script>

<template>
  <div :class="classes" class="e-chat">
    <!-- Array mode: virtual-scrolled message groups -->
    <div v-show="isArrayMode" ref="scrollContainerRef" part="scroll" :class="scrollCls">
      <!-- Load-more sentinel (always in DOM for IntersectionObserver; 1px tall) -->
      <div ref="sentinelRef" style="height: 1px; width: 100%" />
      <!-- Spinner shown at top while loading older messages (pagination) -->
      <div v-show="loading && virtualItems.length > 0" class="flex justify-center py-3" part="loader">
        <e-loader></e-loader>
      </div>

      <!-- Skeleton bubbles shown during initial load (no messages yet) -->
      <div v-if="loading && virtualItems.length === 0" class="flex flex-col gap-1 px-4 py-2" part="skeleton-loader">
        <!-- left group, 2 bubbles -->
        <div class="flex items-end gap-2 py-1">
          <e-skeleton v-if="showAvatar" variant="circle" class="h-8 w-8 flex-shrink-0"></e-skeleton>
          <div class="flex max-w-[75%] flex-col gap-0.5">
            <e-skeleton class="ml-1 h-3 w-20"></e-skeleton>
            <e-skeleton variant="rect" class="h-9 w-48 rounded-lg"></e-skeleton>
            <e-skeleton variant="rect" class="h-9 w-36 rounded-lg"></e-skeleton>
          </div>
        </div>
        <!-- right group, 1 bubble -->
        <div class="flex flex-row-reverse py-1">
          <div class="flex max-w-[75%] flex-col items-end gap-0.5">
            <e-skeleton variant="rect" class="h-9 w-52 rounded-lg"></e-skeleton>
          </div>
        </div>
        <!-- left group, 1 bubble -->
        <div class="flex items-end gap-2 py-1">
          <e-skeleton v-if="showAvatar" variant="circle" class="h-8 w-8 flex-shrink-0"></e-skeleton>
          <div class="flex max-w-[75%] flex-col gap-0.5">
            <e-skeleton class="ml-1 h-3 w-16"></e-skeleton>
            <e-skeleton variant="rect" class="h-9 w-56 rounded-lg"></e-skeleton>
          </div>
        </div>
      </div>

      <!-- Virtual list: outer height container -->
      <div :style="{ height: totalHeight + 'px' }">
        <!-- Inner transform container (GPU-composited positioning) -->
        <div :style="{ transform: `translateY(${topOffset}px)` }">
          <chat-virtual-item
            v-for="vItem in virtualItems"
            :key="vItem.key"
            :v-item="vItem"
            :show-avatar="showAvatar"
            :measure-item="measureItem"
            :should-show-divider="shouldShowDivider"
            :format-time="formatTime"
            :format-divider="formatDivider"
          />
        </div>
      </div>
    </div>

    <!-- Slot mode: consumer composes e-chat-group / e-chat-bubble directly -->
    <div v-show="!isArrayMode" :class="scrollCls" part="scroll">
      <slot />
    </div>
  </div>
</template>

<style>
:host {
  display: contents;
  --chat-user-color: var(--color-primary);
}

@keyframes chat-cursor {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

@keyframes chat-typing {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-25%);
    opacity: 1;
  }
}
</style>
